import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PasswordRecoveryService } from '../../../../core/services/password-recovery.service';

@Component({
  selector: 'app-recover-password',
  imports: [CommonModule, FormsModule, RouterLink, MatProgressSpinnerModule, TranslateModule],
  templateUrl: './recover-password.component.html',
  styleUrl: './recover-password.component.css'
})
export class RecoverPasswordComponent {
  // Steps: 1 - email, 2 - code verification, 3 - new password
  currentStep = signal(1);

  // Step 1 - Email
  email = signal('');

  // Step 2 - Code
  verificationCode = signal('');

  // Step 3 - Password
  newPassword = signal('');
  confirmPassword = signal('');
  showPassword = signal(false);
  showConfirmPassword = signal(false);

  // UI states
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);

  // Store verify code from API
  private verifyCodeToken = signal<string | null>(null);

  constructor(
    private passwordRecoveryService: PasswordRecoveryService,
    private router: Router
  ) {}

  onSendCode(): void {
    if (!this.email() || !this.validateEmail(this.email())) {
      this.errorMessage.set('Пожалуйста, введите корректный email');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.passwordRecoveryService.sendCode(this.email()).subscribe({
      next: (response) => {
        console.log('Code sent:', response);
        this.isLoading.set(false);
        if (response.success) {
          this.successMessage.set('Код подтверждения отправлен на ваш email');
          this.currentStep.set(2);
        } else {
          this.errorMessage.set(response.msg || 'Ошибка при отправке кода');
        }
      },
      error: (error) => {
        console.error('Send code error:', error);
        this.isLoading.set(false);
        this.errorMessage.set('Ошибка при отправке кода. Проверьте email и повторите попытку.');
      }
    });
  }

  onVerifyCode(): void {
    if (!this.verificationCode()) {
      this.errorMessage.set('Пожалуйста, введите код подтверждения');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.passwordRecoveryService.validateCode(this.email(), this.verificationCode()).subscribe({
      next: (response) => {
        console.log('Code validation:', response);
        this.isLoading.set(false);
        if (response.success && response.res) {
          this.verifyCodeToken.set(response.res);
          this.successMessage.set('Код подтвержден. Введите новый пароль');
          this.currentStep.set(3);
        } else {
          this.errorMessage.set(response.msg || 'Неверный код подтверждения');
        }
      },
      error: (error) => {
        console.error('Verify code error:', error);
        this.isLoading.set(false);
        this.errorMessage.set('Неверный код подтверждения');
      }
    });
  }

  onResetPassword(): void {
    if (!this.newPassword() || !this.confirmPassword()) {
      this.errorMessage.set('Пожалуйста, заполните все поля');
      return;
    }

    if (this.newPassword() !== this.confirmPassword()) {
      this.errorMessage.set('Пароли не совпадают');
      return;
    }

    if (this.newPassword().length < 6) {
      this.errorMessage.set('Пароль должен содержать минимум 6 символов');
      return;
    }

    const verifyCode = this.verifyCodeToken();
    if (!verifyCode) {
      this.errorMessage.set('Ошибка: отсутствует код верификации');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.passwordRecoveryService.resetPassword({
      verifyCode: verifyCode,
      newPassword: this.newPassword(),
      confirmPassword: this.confirmPassword()
    }).subscribe({
      next: (response) => {
        console.log('Password reset:', response);
        this.isLoading.set(false);
        if (response.success) {
          this.successMessage.set('Пароль успешно изменен! Переход на страницу входа...');
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 2000);
        } else {
          this.errorMessage.set(response.msg || 'Ошибка при смене пароля');
        }
      },
      error: (error) => {
        console.error('Reset password error:', error);
        this.isLoading.set(false);
        this.errorMessage.set('Ошибка при смене пароля. Попробуйте еще раз.');
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword.update(value => !value);
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.update(value => !value);
  }

  goBack(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update(step => step - 1);
      this.errorMessage.set(null);
      this.successMessage.set(null);
    }
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '');
    input.value = value;
    this.verificationCode.set(value);
  }

  private validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
